/**
 * Bridge between the inspector forms' debounced auto-save and save-as-component.
 *
 * Inspector edits are persisted on a 500ms debounce, but save-as-component makes
 * the server serialize the node straight from the database. A save that overtakes
 * that write snapshots the node as it was BEFORE the edit — a rename typed a
 * moment earlier is silently missing from the library entry.
 *
 * Two things can stand between a keystroke and the database, and a save has to
 * wait for both:
 *   - a debounce timer that has not fired yet   → drained via the flushers
 *   - a write already on the wire (timer fired; PATCH + plan refetch running)
 *                                               → awaited via `lastWrite`
 * Draining only the timer leaves the second, much wider window wide open.
 *
 * More than one form can be mounted at once — a station shows the Detail form
 * and the Training RX form side by side — so every mounted form registers its
 * own flush and a save drains all of them.
 */

export interface PendingNodeEdit {
  /** Id of the node the edits belong to. */
  id: number
  /** Component-library type of that node ('lane' | 'block' | 'week' | …). Ids are
   *  only unique per level, so a caller must match on this too. */
  componentType: string
  /** The debounced patch that was written. */
  data: Record<string, unknown>
}

type PendingEditFlusher = () => Promise<void>

const activeFlushers = new Set<PendingEditFlusher>()

/**
 * The most recent debounced write. Kept after it settles — a save arriving a beat
 * later still needs the values it wrote — but consumed by the first flush that
 * reads it, so a long-stale entry can never override a name that has since been
 * changed through some other path.
 */
let lastWrite: { edit: PendingNodeEdit; accepted: Promise<boolean> } | null = null

/** Register a mounted inspector form's flush. Returns an unregister callback. */
export function registerPendingNodeEditFlusher(flusher: PendingEditFlusher): () => void {
  activeFlushers.add(flusher)
  return () => {
    activeFlushers.delete(flusher)
  }
}

/**
 * Record a debounced write so a save can wait for it even once the debounce timer
 * has already fired.
 *
 * `write` resolving `false` means the server refused the edit — the practice
 * stores return that (rather than throwing) when the edit lock was taken away.
 * Those values never reached the database, so a save must not adopt them.
 */
export function trackNodeWrite(edit: PendingNodeEdit, write: Promise<boolean | void>): Promise<boolean> {
  const accepted = write.then(
    result => result !== false,
    () => false
  )
  lastWrite = { edit, accepted }
  return accepted
}

/**
 * Drain any debounced edit and wait for the write to reach the server. Returns
 * the patch that actually landed, so the caller can prefer it over values it read
 * from the store before the flush — those are still the pre-edit copy.
 *
 * Never rejects: a refused or failed write yields null, and the server then still
 * holds the old values, so saving under those is the correct outcome.
 */
export async function flushPendingNodeEdits(): Promise<PendingNodeEdit | null> {
  // Sequentially: two forms flushing at once would each fire their own write and
  // plan refetch, and the second refetch could land mid-write of the first.
  for (const flusher of [...activeFlushers]) await flusher()
  const write = lastWrite
  lastWrite = null
  if (!write) return null
  return (await write.accepted) ? write.edit : null
}

/**
 * Name the node should be saved under: the just-landed rename when it targets
 * this exact node, else whatever the caller read from the store.
 *
 * The caller captures the name when the button is clicked — i.e. before the
 * flush — so on a fast click that copy is still the pre-rename one.
 */
export function resolveSavedNodeName(
  flushed: PendingNodeEdit | null,
  componentType: string,
  rootId: number,
  fallbackName: string
): string {
  if (!flushed || flushed.id !== rootId || flushed.componentType !== componentType) return fallbackName
  const pendingName = typeof flushed.data.name === 'string' ? flushed.data.name.trim() : ''
  return pendingName || fallbackName
}
